/**
 * Subscription Module Initialization
 * Base module for subscription management with lazy loading
 */

import React, { Suspense } from 'react';
import { Spinner } from '@blueprintjs/core';

// EAGER LOAD: Core services (small, needed immediately for commands)
import { registerSubscriptionCommands } from './services/commands';
import { getCurrentUserFromStore, checkAdminAccess } from './services/auth';
import { setStoreForAPI } from './services/api';
import { setStoreForPaymentService } from './services/paymentService';
import offlineSubscriptionService from './services/offlineService';
import { initDeviceSession } from './services/deviceSession';


// LAZY LOAD: Panel components (large, load on-demand)
const SidePanel = React.lazy(() => import('./panels/SidePanel'));
const Docs = React.lazy(() => import('./panels/Docs'));
const MySubscriptionsPanel = React.lazy(() => import('./panels/MySubscriptionsPanel'));
const PlansManagementPanel = React.lazy(() => import('./panels/PlansManagementPanel'));
const PricingManagementPanel = React.lazy(() => import('./panels/PricingManagementPanel'));
const Dashboard = React.lazy(() => import('./panels/Dashboard'));
const ExtensionsPanel = React.lazy(() => import('./panels/ExtensionsPanel'));
const ExtensionFeaturesPanel = React.lazy(() => import('./panels/ExtensionFeaturesPanel'));
const MetersPanel = React.lazy(() => import('./panels/MetersPanel'));
const SubscriptionsOverviewPanel = React.lazy(() => import('./panels/SubscriptionsOverviewPanel'));
const UsageExplorerPanel = React.lazy(() => import('./panels/UsageExplorerPanel'));
const PaymentSuccess = React.lazy(() => import('./panels/PaymentSuccess'));
const GenericSubscriptionPanel = React.lazy(() => import('./panels/GenericSubscriptionPanel'));
const MyDevicesPanel = React.lazy(() => import('./panels/MyDevicesPanel'));

// LAZY LOAD: Modal components
const PricingModal = React.lazy(() => import('./components/PricingModal'));
const PaymentDialog = React.lazy(() => import('./components/PaymentDialog'));

/**
 * Loading fallback component for lazy-loaded components
 */
const LoadingFallback = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '40px' }}>
        <Spinner size={50} />
    </div>
);

/**
 * Wrapper component for lazy-loaded components with Suspense
 */
const LazyComponent = ({ Component, ...props }) => (
    <Suspense fallback={<LoadingFallback />}>
        <Component {...props} />
    </Suspense>
);


// Access BodaProContext from window (created in UILayout.js)
// Use a getter function to ensure it's available when needed
export const getBodaProContext = () => {
    return window['bodapro-context'] || window.BodaProContext;
    // return window['bodapro-context'];
};

/**
 * Initialize subscription module
 * This function registers all components, commands, and activity bar items
 */
export function initializeSubscription(store) {
    try {
        // Ensure global executable commands registry exists
        globalThis.executableCommands$ = globalThis.executableCommands$ || {};
        
        const BodaProContext = getBodaProContext();

        console.log('initializeSubscription called');   
        console.log('BodaProContext available:', !!BodaProContext);
        console.log('store available:', !!store);
        console.log('executableCommands$ available:', !!globalThis.executableCommands$);
        console.log('executableCommands$ keys:', Object.keys(globalThis.executableCommands$ || {}));
        console.log('globalThis.executableCommands$:', globalThis.executableCommands$);
        
        if (!BodaProContext) {
            console.error('BodaProContext not available - subscription module initialization delayed');
            // Retry after a short delay
            setTimeout(() => {
                if (getBodaProContext()) {
                    initializeSubscription(store);
                }
            }, 100);
            return;
        }
        
        if (!store) {
            console.warn('Redux store not provided to initializeSubscription');
            return;
        }

        // Set store for services
        setStoreForAPI(store);
        setStoreForPaymentService(store);
        offlineSubscriptionService.setStore(store);

        // Setup network reconnect listener for offline sync
        offlineSubscriptionService.setupNetworkReconnectListener();

        // Register executable commands (uses eager-loaded services)
        registerSubscriptionCommands(store);

        // Start device seat enforcement (register on login + heartbeat).
        initDeviceSession(store);

        // Register all components with lazy loading
        // Legacy component (keep for compatibility)
        BodaProContext.registerComponent({
            id: 'bss.subscription',
            label: 'subscription',
            icon: 'lightning',
            component: (props) => <LazyComponent Component={SidePanel} {...props} />
        });

        BodaProContext.registerComponent({
            id: 'bss.subscription:docs',
            label: 'Docs',
            icon: 'lightning',
            component: (props) => <LazyComponent Component={Docs} {...props} />
        });

        // Register PricingModal component (lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-pricing-modal',
            label: 'Subscription Plans',
            icon: 'shopping-cart',
            component: (props) => <LazyComponent Component={PricingModal} {...props} />
        });

        // Register MySubscriptionsPanel component (lazy loaded)
        BodaProContext.registerComponent({
            id: 'my-subscriptions-panel',
            label: 'My Subscriptions',
            icon: 'credit-card',
            component: (props) => <LazyComponent Component={MySubscriptionsPanel} {...props} />
        });

        // Register PlansManagementPanel component (Admin, lazy loaded)
        BodaProContext.registerComponent({
            id: 'plans-management-panel',
            label: 'Plans Management',
            icon: 'properties',
            component: (props) => <LazyComponent Component={PlansManagementPanel} {...props} />
        });

        // Register PricingManagementPanel component (Admin, lazy loaded)
        BodaProContext.registerComponent({
            id: 'pricing-management-panel',
            label: 'Pricing Management',
            icon: 'dollar',
            component: (props) => <LazyComponent Component={PricingManagementPanel} {...props} />
        });

        // Register Dashboard component (Admin, lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-dashboard',
            label: 'Dashboard',
            icon: 'dashboard',
            component: (props) => <LazyComponent Component={Dashboard} {...props} />
        });

        // Register ExtensionsPanel component (Admin, lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-extensions-panel',
            label: 'Extensions',
            icon: 'application',
            component: (props) => <LazyComponent Component={ExtensionsPanel} {...props} />
        });

        // Register ExtensionFeaturesPanel component (Admin, lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-features-panel',
            label: 'Features Catalog',
            icon: 'tag',
            component: (props) => <LazyComponent Component={ExtensionFeaturesPanel} {...props} />
        });

        // Register MetersPanel component (Admin, lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-meters-panel',
            label: 'Meters',
            icon: 'timeline-line-chart',
            component: (props) => <LazyComponent Component={MetersPanel} {...props} />
        });

        // Register SubscriptionsOverviewPanel component (Admin, lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-overview-panel',
            label: 'All Subscriptions',
            icon: 'database',
            component: (props) => <LazyComponent Component={SubscriptionsOverviewPanel} {...props} />
        });

        // Register UsageExplorerPanel component (Admin, lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-usage-panel',
            label: 'Usage Explorer',
            icon: 'chart',
            component: (props) => <LazyComponent Component={UsageExplorerPanel} {...props} />
        });

        // Register PaymentSuccess component (lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-payment-success',
            label: 'Payment Confirmation',
            icon: 'tick-circle',
            component: (props) => <LazyComponent Component={PaymentSuccess} {...props} />
        });

        // Register GenericSubscriptionPanel component (lazy loaded)
        BodaProContext.registerComponent({
            id: 'subscription-generic-panel',
            label: 'Subscription',
            icon: 'credit-card',
            component: (props) => <LazyComponent Component={GenericSubscriptionPanel} {...props} />
        });

        // Register MyDevicesPanel component (lazy loaded)
        BodaProContext.registerComponent({
            id: 'my-devices-panel',
            label: 'My Devices',
            icon: 'desktop',
            component: (props) => <LazyComponent Component={MyDevicesPanel} {...props} />
        });

        // Register commands
        // Legacy command (keep for compatibility)
        BodaProContext.registerCommand('bss.subscription', function (params) {
            BodaProContext.addPanel({
                id: 'bss.subscription',
                title: 'Subscription',
                icon: 'list',
                componentName: 'bss.subscription',
            });
        });

        // Register command to open subscription side panel
        BodaProContext.registerCommand('subscription.sidepanel', function (params) {
            // Open the subscription side panel in primary-sidebar
            BodaProContext.addPanel({
                id: 'bss.subscription',
                title: 'Subscription',
                icon: 'credit-card',
                componentName: 'bss.subscription',
                groupId: 'primary-sidebar',
                replacePanel: true,
            });
            
            // Show the primary sidebar if it's hidden
            if (BodaProContext.showSection) {
                BodaProContext.showSection('primary-sidebar');
            } else if (BodaProContext.toggleSection) {
                // If showSection doesn't exist, try to ensure sidebar is visible
                BodaProContext.toggleSection('primary-sidebar');
            }
        });

        // Register command to open subscription panel (with smart routing)
        // Register as both event command and executable command for compatibility
        BodaProContext.registerCommand('subscription.open', function (params) {
            // Get user from store using selector
            const user = getCurrentUserFromStore(store);
            // Check if user is admin
            const isAdmin = checkAdminAccess(user);

            if (isAdmin) {
                // Admins see plans management by default
                BodaProContext.addPanel({
                    id: 'subscription-admin',
                    title: 'Subscription Management',
                    icon: 'properties',
                    componentName: 'plans-management-panel',
                });
            } else {
                // Regular users see their subscriptions
                BodaProContext.addPanel({
                    id: 'my-subscriptions',
                    title: 'My Subscriptions',
                    icon: 'credit-card',
                    componentName: 'my-subscriptions-panel',
                    groupId: 'main-panel',
                });
            }
        });

        // Register executable command for subscription side panel
        const subscriptionSidePanelCommand = async function (params) {
            // Open the subscription side panel in primary-sidebar
            BodaProContext.addPanel({
                id: 'bss.subscription',
                title: 'Subscription',
                icon: 'credit-card',
                componentName: 'bss.subscription',
                groupId: 'primary-sidebar',
                replacePanel: true,
            });
            
            // Show the primary sidebar if it's hidden
            if (BodaProContext.showSection) {
                BodaProContext.showSection('primary-sidebar');
            } else if (BodaProContext.toggleSection) {
                BodaProContext.toggleSection('primary-sidebar');
            }
            
            return { success: true };
        };

        // Also register as executable command for executeCommand() calls (main panel version)
        const subscriptionOpenCommand = async function (params) {
            // Get user from store using selector
            const user = getCurrentUserFromStore(store);
            // Check if user is admin
            const isAdmin = checkAdminAccess(user);

            if (isAdmin) {
                // Admins see plans management by default
                BodaProContext.addPanel({
                    id: 'subscription-admin',
                    title: 'Subscription Management',
                    icon: 'properties',
                    componentName: 'plans-management-panel',
                });
            } else {
                // Regular users see their subscriptions
                BodaProContext.addPanel({
                    id: 'my-subscriptions',
                    title: 'My Subscriptions',
                    icon: 'credit-card',
                    componentName: 'my-subscriptions-panel',
                    groupId: 'main-panel',
                });
            }
            
            return { success: true };
        };
        
        // Ensure global registry exists
        globalThis.executableCommands$ = globalThis.executableCommands$ || {};
        
        // Register side panel command
        globalThis.executableCommands$['subscription.sidepanel'] = subscriptionSidePanelCommand;
        if (BodaProContext.registerExecutableCommand) {
            BodaProContext.registerExecutableCommand('subscription.sidepanel', subscriptionSidePanelCommand);
        }
        console.log('✓ subscription.sidepanel command registered');
        
        // Register main panel command
        globalThis.executableCommands$['subscription.open'] = subscriptionOpenCommand;
        if (BodaProContext.registerExecutableCommand) {
            BodaProContext.registerExecutableCommand('subscription.open', subscriptionOpenCommand);
        }
        console.log('✓ subscription.open command registered');

        // Register command to open plans management (admin only)
        BodaProContext.registerCommand('subscription.admin.plans', async function (params) {
            BodaProContext.addPanel({
                id: 'subscription-plans-admin',
                title: 'Plans Management',
                icon: 'properties',
                componentName: 'plans-management-panel',
                groupId: 'main-panel',
                params
            });
        });

        // Register command to open pricing management (admin only)
        BodaProContext.registerCommand('subscription.admin.pricing', async function (params) {
            BodaProContext.addPanel({
                id: 'subscription-pricing-admin',
                title: 'Pricing Management',
                icon: 'dollar',
                componentName: 'pricing-management-panel',
                groupId: 'main-panel',
            });
        });

        // Register command to open user subscriptions
        BodaProContext.registerCommand('subscription.my', async function (params) {
            BodaProContext.addPanel({
                id: 'my-subscriptions',
                title: 'My Subscriptions',
                icon: 'credit-card',
                componentName: 'my-subscriptions-panel',
                groupId: 'main-panel',
            });
        });

        // Register command to open dashboard
        BodaProContext.registerCommand('subscription.dashboard', async function (params) {
            BodaProContext.addPanel({
                id: 'subscription-dashboard',
                title: 'Subscription Dashboard',
                icon: 'dashboard',
                componentName: 'subscription-dashboard',
                groupId: 'main-panel',
            });
        });

        // Register command to open extensions panel
        BodaProContext.registerCommand('subscription.admin.extensions', async function (params) {
            BodaProContext.addPanel({
                id: 'subscription-extensions',
                title: 'Extensions',
                icon: 'application',
                componentName: 'subscription-extensions-panel',
                groupId: 'main-panel',
            });
        });

        // Register command to open features panel
        BodaProContext.registerCommand('subscription.admin.features', async function (params) {
            BodaProContext.addPanel({
                id: 'subscription-features',
                title: 'Extension Features',
                icon: 'tag',
                componentName: 'subscription-features-panel',
                props: params,
                groupId: 'main-panel',
            });
        });

        // Register command to open meters panel
        BodaProContext.registerCommand('subscription.admin.meters', async function (params) {
            BodaProContext.addPanel({
                id: 'subscription-meters',
                title: 'Usage Meters',
                icon: 'chart',
                componentName: 'subscription-meters-panel',
                props: params,
                groupId: 'main-panel',
            });
        });

        // Register command to open subscriptions overview
        BodaProContext.registerCommand('subscription.admin.subscriptions', async function (params) {
            BodaProContext.addPanel({
                id: 'subscription-overview',
                title: 'All Subscriptions',
                icon: 'people',
                componentName: 'subscription-overview-panel',
                groupId: 'main-panel',
            });
        });

        // Register command to open usage explorer
        BodaProContext.registerCommand('subscription.admin.usage', async function (params) {
            BodaProContext.addPanel({
                id: 'subscription-usage',
                title: 'Usage Explorer',
                icon: 'database',
                componentName: 'subscription-usage-panel',
                groupId: 'main-panel',
            });
        });

        // Register command to open the device manager ("My Devices").
        // Also triggered automatically on a 409 seat_limit_reached or a revoked seat.
        BodaProContext.registerCommand('subscription.devices.manage', function (params) {
            BodaProContext.addPanel({
                id: 'my-devices',
                title: 'My Devices',
                icon: 'desktop',
                componentName: 'my-devices-panel',
                groupId: 'main-panel',
                params: params || {}
            });
        });

        // Register command to open generic subscription panel
        BodaProContext.registerCommand('subscription.panel.generic', async function (params) {
            const { extensionKey, title } = params || {};
            if (!extensionKey) {
                console.error('subscription.panel.generic requires extensionKey');
                return;
            }

            BodaProContext.addPanel({
                id: `subscription-panel-${extensionKey}`,
                title: title || `${extensionKey} Subscription`,
                icon: 'credit-card',
                componentName: 'subscription-generic-panel',
                groupId: 'main-panel',
                params: params
            });
        });

        // Register activity bar item
        // BodaProContext.registerActivityBarItem({
        //     id: 'subscription',
        //     label: 'Subscriptions',
        //     icon: 'credit-card',
        //     position: 10, // Position in activity bar
        //     onClick: () => {
        //         // Get user from store using selector
        //         const user = getCurrentUserFromStore(store);
        //         // Smart routing based on user role
        //         const isAdmin = checkAdminAccess(user);

        //         if (isAdmin) {
        //             // Show admin menu
        //             BodaProContext.showContextMenu({
        //                 items: [
        //                     {
        //                         label: 'Dashboard',
        //                         icon: 'dashboard',
        //                         onClick: () => BodaProContext.executeCommand('subscription.dashboard')
        //                     },
        //                     { type: 'separator' },
        //                     {
        //                         label: 'Extensions',
        //                         icon: 'application',
        //                         onClick: () => BodaProContext.executeCommand('subscription.admin.extensions')
        //                     },
        //                     {
        //                         label: 'Plans Management',
        //                         icon: 'properties',
        //                         onClick: () => BodaProContext.executeCommand('subscription.admin.plans')
        //                     },
        //                     {
        //                         label: 'Pricing Management',
        //                         icon: 'dollar',
        //                         onClick: () => BodaProContext.executeCommand('subscription.admin.pricing')
        //                     },
        //                     {
        //                         label: 'Features Catalog',
        //                         icon: 'tag',
        //                         onClick: () => BodaProContext.executeCommand('subscription.admin.features')
        //                     },
        //                     {
        //                         label: 'Usage Meters',
        //                         icon: 'chart',
        //                         onClick: () => BodaProContext.executeCommand('subscription.admin.meters')
        //                     },
        //                     { type: 'separator' },
        //                     {
        //                         label: 'All Subscriptions',
        //                         icon: 'people',
        //                         onClick: () => BodaProContext.executeCommand('subscription.admin.subscriptions')
        //                     },
        //                     {
        //                         label: 'Usage Explorer',
        //                         icon: 'database',
        //                         onClick: () => BodaProContext.executeCommand('subscription.admin.usage')
        //                     },
        //                     { type: 'separator' },
        //                     {
        //                         label: 'My Subscriptions',
        //                         icon: 'credit-card',
        //                         onClick: () => BodaProContext.executeCommand('subscription.my')
        //                     }
        //                 ]
        //             });
        //         } else {
        //             // Regular users go directly to their subscriptions
        //             BodaProContext.executeCommand('subscription.my');
        //         }
        //     }
        // });

        console.log('Subscription module initialized with lazy loading');
    } catch (error) {
        console.error('Failed to initialize subscription module:', error);
        throw error;
    }
}

export default initializeSubscription;

